// PortalProvider — SPA-scoped portal context.
//
// Portal is a property of the SPA bundle, not the screen. Admin's bundle
// only ever runs at admin.*; consumer's bundle only ever runs at app.* (and
// future tenant subdomains). Threading `portal="admin"` through every
// screen is repetitive and error-prone — the SPA declares its portal once
// at the root and downstream components read it from context.
//
// Compose at the SPA root:
//   <PortalProvider portal="admin">
//     <App />
//   </PortalProvider>
//
// Downstream consumers (AuthLayout today; possibly a portal-aware shell
// or branding component later) read it via usePortal() or as an internal
// context fallback.
//
// The `portal` prop on AuthLayout still wins over context — useful for
// design-system isolation (Storybook, snapshots, design-system docs) where
// neither portal context is set up.

import { createContext, useContext, type ReactNode } from 'react';
import type { AuthLayoutPortal } from './AuthLayout.js';

export const PortalContext = createContext<AuthLayoutPortal | undefined>(undefined);
PortalContext.displayName = 'PortalContext';

export interface PortalProviderProps {
  portal: AuthLayoutPortal;
  children: ReactNode;
}

export function PortalProvider({ portal, children }: PortalProviderProps) {
  return <PortalContext.Provider value={portal}>{children}</PortalContext.Provider>;
}

/** Read the current portal from context. Returns `undefined` if no
 *  `<PortalProvider>` wraps the call site — callers decide how to fall back. */
export function usePortal(): AuthLayoutPortal | undefined {
  return useContext(PortalContext);
}
